import { type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SideDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Accessible title; rendered visually-hidden if `header` is supplied. */
  title: string;
  /**
   * Accessible description. Omit when the panel's own content is self-describing —
   * Radix then needs an explicit `aria-describedby={undefined}` opt-out, which this
   * component supplies, otherwise it logs a missing-description warning.
   */
  description?: string;
  className?: string;
}

/**
 * Right-side slide-over panel (no Sheet primitive exists in the kit). Built on
 * Radix Dialog so focus-trap, ESC, and outside-click close come for free.
 */
export function SideDrawer({
  open,
  onOpenChange,
  children,
  title,
  description,
  className,
}: SideDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 right-0 z-modal flex h-full w-full max-w-xl flex-col border-l border-border/60 bg-background shadow-2xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-200',
            className,
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {description && (
            <DialogPrimitive.Description className="sr-only">
              {description}
            </DialogPrimitive.Description>
          )}
          {/* Above the scroll container (z-10), not level with it: a panel with a
              sticky header would otherwise paint its opaque background over this. */}
          <DialogPrimitive.Close className="absolute right-4 top-4 z-20 rounded-md bg-background/80 p-1 text-muted-foreground opacity-70 backdrop-blur-sm transition-opacity hover:bg-muted hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
          <div className="flex-1 overflow-y-auto">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
